import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Loader2, MapPin, Globe, Phone, BadgeCheck, ShieldCheck, Lock, Landmark, MessageSquare, Mail, ArrowLeft, Send } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { stateName, cityPath } from "@/lib/constants";
import { QILogo } from "@/components/site/QILogo";
import { ContactDialog } from "@/components/site/ContactDialog";
import { ReferralDialog } from "@/components/site/ReferralDialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const Trust = ({ on, icon: Icon, label }) => (
  <div className={`flex items-center gap-3 rounded-2xl px-4 py-3 border ${on ? "border-qi-emerald/30 bg-qi-emerald/10 text-white" : "border-white/10 bg-white/[0.02] text-white/35"}`}>
    <Icon size={18} className={on ? "text-qi-emerald" : ""} />
    <span className="text-sm font-semibold">{label}</span>
    <span className="ml-auto font-mono text-[10px] uppercase">{on ? "Yes" : "Not listed"}</span>
  </div>
);

const Coverage = ({ qi }) => (
  <div className="glass-card p-7">
    <span className="eyebrow">Coverage</span>
    {qi.nationwide ? <p className="mt-4 font-display text-xl font-bold">Nationwide — all 50 states</p> : (
      <div className="flex flex-wrap gap-2 mt-4">
        {(qi.states_served?.length ? qi.states_served : [qi.state]).map((s) => (
          <span key={s} className="text-xs bg-white/5 border border-white/10 rounded-full px-3 py-1.5">{stateName(s)}</span>
        ))}
      </div>
    )}
    {qi.metros_served?.length > 0 && (
      <>
        <span className="eyebrow block mt-8">Local metros</span>
        <div className="flex flex-wrap gap-2 mt-4" data-testid="qi-profile-metros">
          {qi.metros_served.map((m) => {
            const [city, code] = m.split(", ");
            return <Link key={m} to={cityPath(city, code)} className="text-xs bg-qi-emerald/10 border border-qi-emerald/25 text-white rounded-full px-3 py-1.5 hover:border-qi-emerald/60">{m}</Link>;
          })}
        </div>
      </>
    )}
    <span className="eyebrow block mt-8">Exchange types</span>
    <div className="flex flex-wrap gap-2 mt-4">
      {(qi.services || []).map((s) => <span key={s} className="text-xs bg-qi-pink/10 border border-qi-pink/25 text-white rounded-full px-3 py-1.5">{s}</span>)}
    </div>
  </div>
);

export default function QIProfile() {
  const { slug } = useParams();
  const [qi, setQi] = useState(null);
  const [open, setOpen] = useState(false);
  const [refOpen, setRefOpen] = useState(false);
  const { user, profile, partner } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/qis/${slug}`).then(({ data }) => setQi(data)).catch(() => setQi(false));
  }, [slug]);

  const startChat = async () => {
    if (!user) return navigate("/login", { state: { from: `/qi/${slug}` } });
    try {
      const { data } = await api.post("/chat/conversations", { profile_id: qi.profile_id });
      navigate(`/chat?c=${data.conversation_id}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (qi === null) return <div className="py-32 flex justify-center"><Loader2 className="animate-spin text-qi-pink" /></div>;
  if (!qi) return <div className="py-32 text-center text-white/60" data-testid="qi-not-found">Listing not found.</div>;
  const isOwn = profile && profile.profile_id === qi.profile_id;
  const canChat = user && !qi.is_sample && !isOwn;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="qi-profile-page">
      <Link to="/directory" className="text-sm text-white/50 hover:text-white inline-flex items-center gap-1.5"><ArrowLeft size={14} /> Back to directory</Link>
      <div className="relative overflow-hidden glass-card p-8 md:p-10 mt-6">
        <div className="absolute -top-24 -right-24 w-80 h-80 bg-qi-pink/15 rounded-full blur-3xl" />
        <div className="relative flex flex-col md:flex-row md:items-center gap-6">
          <QILogo profile={qi} size="w-24 h-24" text="text-3xl" />
          <div className="flex-1 min-w-0">
            {qi.is_sample && <span className="text-[10px] font-mono uppercase tracking-wider bg-qi-gold/15 text-qi-gold rounded-full px-2.5 py-1">Sample listing</span>}
            {qi.is_founding && <span data-testid="qi-profile-founding-badge" className="text-[10px] font-bold uppercase tracking-wider bg-qi-gold/15 text-qi-gold rounded-full px-2.5 py-1">Founding member</span>}
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-2" data-testid="qi-profile-name">{qi.company_name}</h1>
            <p className="text-white/55 mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm">
              <span className="inline-flex items-center gap-1.5"><MapPin size={14} />{qi.city ? `${qi.city}, ` : ""}{stateName(qi.state)}</span>
              {qi.years_in_business > 0 && <span>{qi.years_in_business} yrs in business</span>}
              {qi.exchanges_per_year && <span>{qi.exchanges_per_year} exchanges / yr</span>}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <button onClick={() => setOpen(true)} disabled={qi.is_sample || isOwn} className="btn-pink" data-testid="qi-card-contact-button"><Mail size={16} /> Contact this QI</button>
            {(canChat || !user) && !qi.is_sample && <button onClick={startChat} className="btn-ghost" data-testid="qi-card-chat-button"><MessageSquare size={16} /> Chat</button>}
            {partner?.status === "approved" && !qi.is_sample && <button onClick={() => setRefOpen(true)} className="btn-ghost" data-testid="qi-send-referral-button"><Send size={16} /> Send a referral</button>}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-6 mt-6">
        <div className="lg:col-span-7 space-y-6">
          <div className="glass-card p-7">
            <span className="eyebrow">About the firm</span>
            <p className="mt-4 text-white/75 leading-relaxed whitespace-pre-line" data-testid="qi-profile-bio">{qi.bio}</p>
            <div className="mt-6 pt-6 border-t border-white/10 grid sm:grid-cols-2 gap-3 text-sm text-white/60">
              <span>{qi.contact_name}{qi.title ? ` · ${qi.title}` : ""}</span>
              {qi.website && <a href={qi.website.startsWith("http") ? qi.website : `https://${qi.website}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-qi-pink" data-testid="qi-profile-website"><Globe size={14} />{qi.website}</a>}
              {qi.phone && <span className="inline-flex items-center gap-1.5"><Phone size={14} />{qi.phone}</span>}
            </div>
          </div>
          <Coverage qi={qi} />
        </div>
        <div className="lg:col-span-5 glass-card p-7 h-fit">
          <span className="eyebrow">Safeguards & credentials</span>
          <div className="space-y-3 mt-5">
            <Trust on={qi.fea_member} icon={BadgeCheck} label="Federation of Exchange Accommodators member" />
            <Trust on={qi.fidelity_bond} icon={ShieldCheck} label={`Fidelity bond${qi.bond_amount ? ` (${qi.bond_amount})` : ""}`} />
            <Trust on={qi.eo_insurance} icon={Lock} label="Errors & omissions insurance" />
            <Trust on={qi.segregated_accounts} icon={Landmark} label="Segregated / qualified escrow accounts" />
          </div>
          {qi.certifications?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-6">{qi.certifications.map((c) => <span key={c} className="text-xs font-bold bg-white/10 rounded-full px-3 py-1.5">{c}</span>)}</div>
          )}
          <p className="text-[11px] text-white/35 mt-6 leading-relaxed">Credentials are self-reported by the intermediary. Always verify bonding and escrow arrangements before signing an exchange agreement.</p>
        </div>
      </div>
      <ContactDialog open={open} onOpenChange={setOpen} qi={qi} />
      <ReferralDialog open={refOpen} onOpenChange={setRefOpen} qi={qi} />
    </div>
  );
}
